import type { IconProps } from '@launchpad-ui/icons';
import { Icon } from '@launchpad-ui/icons';

import styles from './BrandArrow.module.css';

type BrandArrowProps = {
  /** Icon size. Defaults to `small`, the convention for icons inside buttons. */
  size?: IconProps['size'];
};

/**
 * Trailing brand arrow for a deliberately scarce set of primary CTAs. Hidden until a Button
 * experiment is running, so callers can include it unconditionally.
 *
 * Gated in CSS off the `data-button-experiment` attribute rather than by reading the flag here,
 * which puts it on the same switch as `static/ld/stylesheets/button-experiments.css`: one lever
 * turns a whole look on, including when the attribute is set by hand to preview one locally.
 * Reading the flag directly meant the arrow silently stayed behind whenever the rest of a look
 * was being previewed that way.
 *
 * Decorative: `Icon` renders `aria-hidden`, so this never changes a button's
 * accessible name.
 *
 * Nudges 4px to the right on hover and keyboard focus, off the containing button's
 * React Aria state attributes — so it only animates when it trails an actual
 * `Button`/`LinkButton`.
 *
 * Reserve this for primary create and forward-motion CTAs. A button that already
 * has an icon does not get an arrow, neither does one whose `variant` is not
 * literally `primary`, and neither does the fused half of a split button — the
 * arrow would point at a border it cannot move past.
 */
export const BrandArrow = ({ size = 'small' }: BrandArrowProps) => (
  <Icon name="arrow-brand-right" size={size} className={styles.arrow} />
);
